import { zodResolver } from "@hookform/resolvers/zod";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Divider, Grid2, IconButton, InputAdornment, Paper, TextField, Toolbar, Typography } from "@mui/material";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useFormStyles } from "../../hooks/useFormStyles";
import { getUpdateUserPasswordFormSchema, UpdateUserPasswordForm } from "../../types/forms/admin/user";
import { getUpdateUserProfileFormSchema, UpdateUserProfileForm } from "../../types/forms/admin/user/UpdateUserProfileForm";
import { UpdateUserPasswordRequest, UpdateUserRequest, User } from "../../types/model/user";

interface UserProfileFormFormProps {
  userData: User;
  onUpdate: (values: UpdateUserRequest) => Promise<boolean>; 
  onUpdatePassword: (values: UpdateUserPasswordRequest) => void;
}

export default function UserProfileForm({
  userData,
  onUpdate,
  onUpdatePassword
}: Readonly<UserProfileFormFormProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [updatePassword, setUpdatePassword] = useState(false);

  const formStyles = useFormStyles(isEditing || updatePassword);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const updateUserValidationSchema = getUpdateUserProfileFormSchema(t);
  const updateUserPasswordValidationSchema = getUpdateUserPasswordFormSchema(t);

  const updateUserMethods = useForm<UpdateUserProfileForm>({
    resolver: zodResolver(updateUserValidationSchema),
    defaultValues: {
      name: userData.name || "",
      email: userData.email || "",
      phoneNumber: userData.phoneNumber || "",
    }
  });

  const updateUserPasswordMethods = useForm<UpdateUserPasswordForm>({
    resolver: zodResolver(updateUserPasswordValidationSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const handleCancelEdit = () => {
    updateUserMethods.reset();
    setIsEditing(false);
  };

  const handleSubmitUser = async (values: UpdateUserProfileForm) => {
    if (!userData) return;

    const requestBody: UpdateUserRequest = {
      ...values,
      roleId: userData.roleId,
      enabled: userData.enabled,
    };

    const success = await onUpdate(requestBody);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleSubmitPassword = (values: UpdateUserPasswordForm) => {
    onUpdatePassword(values);
    updateUserPasswordMethods.reset();
    setShowPassword(false);
    setShowConfirm(false);
    setUpdatePassword(false);
  };

  const handleCancelPassword = () => {
    setUpdatePassword(false);
    updateUserPasswordMethods.reset();
    setShowPassword(false);
    setShowConfirm(false);
  };

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <FormProvider {...updateUserMethods}>
        <Box component="form" id="user-profile-form" onSubmit={updateUserMethods.handleSubmit(handleSubmitUser)}>
          <Toolbar sx={{ mb: 3 }}>
            <Typography sx={{ flex: "1 1 100%" }} variant="h5">
              {t("admin.users.my-account-header")}
            </Typography>

            <Button
              type="button"
              variant="contained"
              startIcon={<ArrowBackIcon />}
              sx={{ textTransform: "none", borderRadius: 2 }}
              onClick={() => navigate(-1)}
            >
              {t("generic.back")}
            </Button>
          </Toolbar>
        
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <TextField
                fullWidth
                label={`${t("admin.users.name")} *`}
                variant={isEditing ? "outlined" : "standard"}
                disabled={!isEditing}
                sx={{ ...formStyles }}
                {...updateUserMethods.register("name")}
                error={!!updateUserMethods.formState.errors.name}
                helperText={updateUserMethods.formState.errors.name?.message as any}
              />
            </Grid2>

            <Grid2 size={6}>
              <TextField
                fullWidth
                label={`${t("admin.users.email")} *`}
                variant={isEditing ? "outlined" : "standard"}
                disabled={!isEditing}
                sx={{ ...formStyles }}
                {...updateUserMethods.register("email")}
                error={!!updateUserMethods.formState.errors.email}
                helperText={updateUserMethods.formState.errors.email?.message as any}

              />
            </Grid2>
            <Grid2 size={6}>
              <TextField
                fullWidth
                label={t("admin.users.phone-number")}
                variant={isEditing ? "outlined" : "standard"}
                disabled={!isEditing}
                sx={{ ...formStyles }}
                {...updateUserMethods.register("phoneNumber")}
                error={!!updateUserMethods.formState.errors.phoneNumber}
                helperText={updateUserMethods.formState.errors.phoneNumber?.message as any}
              />
            </Grid2>
          </Grid2>
        </Box>
      </FormProvider>

      {/* Action Buttons */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          p: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mt: 1,
        }}
      >
        {!isEditing && !updatePassword && (
          <>
            <Button variant="outlined" onClick={() => setIsEditing(true)}>
              {t("generic.edit")}
            </Button>
            <Button variant="outlined" onClick={() => setUpdatePassword(true)}>
              {t("admin.users.user-update-password")}
            </Button>
          </>
        )}

        {isEditing && (
          <>
            <Button variant="outlined" color="secondary" onClick={handleCancelEdit}>
              {t("generic.cancel")}
            </Button>
            <Button type="submit" form="user-profile-form" variant="outlined">
              {t("generic.save")}
            </Button>
          </>
        )}
      </Box>

      {/* Update Password Form */}
      {updatePassword && (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Toolbar>
            <Typography variant="h6" sx={{ flex: 1 }}>
              {t("admin.users.user-update-password")}
            </Typography>
          </Toolbar>

          <Divider />

          <Box sx={{ p: 3 }}>
            <FormProvider {...updateUserPasswordMethods}>
              <form
                id="update-password-form"
                onSubmit={updateUserPasswordMethods.handleSubmit(handleSubmitPassword)}
              >
                <Grid2 container spacing={3}>
                  {/* Password */}
                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      label={`${t("admin.users.password")} *`}
                      variant="outlined"
                      sx={{ ...formStyles }}
                      {...updateUserPasswordMethods.register("password")}
                      error={!!updateUserPasswordMethods.formState.errors.password}
                      helperText={updateUserPasswordMethods.formState.errors.password?.message}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword((prev) => !prev)}
                                edge="end"
                                aria-label={
                                  showPassword
                                    ? t("admin.users.hide-password")
                                    : t("admin.users.show-password")
                                }
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Grid2>

                  {/* Confirm Password */}
                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      type={showConfirm ? "text" : "password"}
                      label={`${t("admin.users.password-confirm")} *`}
                      variant="outlined"
                      sx={{ ...formStyles }}
                      {...updateUserPasswordMethods.register("confirmPassword")}
                      error={!!updateUserPasswordMethods.formState.errors.confirmPassword}
                      helperText={updateUserPasswordMethods.formState.errors.confirmPassword?.message}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowConfirm((prev) => !prev)}
                                edge="end"
                                aria-label={
                                  showConfirm
                                    ? t("admin.users.hide-password")
                                    : t("admin.users.show-password")
                                }
                              >
                                {showConfirm ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Grid2>
                </Grid2>

                <Box
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                  sx={{ mt: 3 }}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleCancelPassword}
                  >
                    {t("generic.cancel")}
                  </Button>

                  <Button
                    type="submit"
                    form="update-password-form"
                    variant="contained"
                  >
                    {t("admin.users.user-update-password")}
                  </Button>
                </Box>
              </form>
            </FormProvider>
          </Box>
        </Paper>
      )}
    </Paper>
  );
}